<template>
  <div class="login-wrap">
    <img :src="require('@/assets/msp-logo.png')" alt="" class="logo">
    <div class="login-input-wrap">
      <h1>{{$t('login.registration_heading')}}</h1>
      <h5 class="text-regular">{{$t('login.registration_text')}}</h5>
      <p class="error" v-if="loginDisabled">{{$t('login.failed_login_attempts')}} ({{disabledTime}})</p>
      <el-form :model="login"
               class="input-form"
               :rules="rules"
               @submit.native.prevent>
        <div class="input-wrap" v-show="step === 'username'">
          <el-form-item
            class="login-input"
            :label="$t('login.username_label')"
            prop="username">
            <el-input
              id="username"
              name="username"
              autocomplete="username"
              :placeholder="$t('login.username_placeholder')"
              v-model="login.username"
              @keyup.native.enter="submitUsername()"
              :disabled="loginDisabled"></el-input>
          </el-form-item>
        </div>

        <div class="identified-user" v-if="step !== 'username'">
          {{ login.username }}
        </div>

        <div class="input-wrap" v-show="step === 'password'">
          <el-form-item
            class="login-input"
            :label="$t('login.password_label')"
            prop="password">
            <el-input id="password"
                      name="password"
                      type="password"
                      autocomplete="current-password"
                      :placeholder="$t('login.password_placeholder')"
                      v-model="login.password"
                      @keyup.native.enter="dologin()"
                      :disabled="loginDisabled"></el-input>
          </el-form-item>
        </div>

        <base-button v-if="step === 'username'"
                     :disabled="loginDisabled"
                     :loading="isLoading"
                     class="login-button"
                     type="primary"
                     @click="submitUsername()">
          {{$t('base.continue')}}
        </base-button>

        <base-button v-if="step === 'password'"
                     :disabled="loginDisabled"
                     :loading="isLoading"
                     class="login-button"
                     type="primary"
                     @click="dologin()">
          {{$t('login.login_action')}}
        </base-button>

        <base-button v-if="step === 'entra'"
                     :loading="isLoading"
                     class="login-button"
                     type="primary"
                     @click="startMicrosoftLogin()">
          {{$t('login.microsoft_login')}}
        </base-button>

        <base-button v-if="step !== 'username'"
                     class="back-button"
                     type="text"
                     :disabled="isLoading"
                     @click="resetToUsername()">
          {{$t('login.back')}}
        </base-button>
      </el-form>
    </div>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import moment from 'moment';

const authStateMappers = createNamespacedHelpers('auth');
import {setLoginFailData, getLoginFailData, removeLoginFailData} from "@/helper/LocalStorageHelper";
import {authService} from "@/services/AuthService.class";
import {loginWithMicrosoftRedirect, getEntraLoginError} from "@/helper/EntraLoginHelper";
import BaseButton from "@/components/common/BaseButton";


    export default {
        name: "Login",
        components: { BaseButton },
        data() {
            let checkPassword = (rule, value, callback) => {
                if (value.length < 4) {
                    return callback(new Error('Please input the age'));
                }
                callback();
            };
            return {
                rules: {
                    username: [
                        {required: true, message: this.$t('login.username_required'), trigger: ['blur', 'change']}
                    ],
                    password: [
                        {
                            required: true,
                            validator: checkPassword,
                            message: this.$t('login.password_required'),
                            trigger: ['blur', 'change']
                        },
                    ],
                },
                isLoading: false,
              step: 'username',
              entraConfig: null,
                login: {
                    username: '',
                    password: ''
                },
                loginDisabled: false,
                disabledTime: 0
            }
        },
        mounted() {},
        created() {
            this.checkDisabledLogin(); // check on the start if the login is disabled
            this.showEntraError();
        },
        methods: {
            disableLogin() {
                this.loginDisabled = false;
            },
            enableLogin() {
                this.loginDisabled = false;
            },
            checkDisabledLogin() {

                // load the data for the failed attempts
                let failedLoginData = getLoginFailData();

                if (failedLoginData && failedLoginData.disabledTime) { // check if a disabled time is set
                    let currentTime = moment();
                    let disabledTime = moment(failedLoginData.disabledTime);

                    if (currentTime.isBefore(disabledTime)) {  // the login needs to be disabled
                        this.disableLogin();
                        let updateInterval = setInterval(() => { // check every second if the login needs to be enabled
                            currentTime = moment();

            if (currentTime > disabledTime) { // the time is over -> enable the login
              clearInterval(updateInterval);
              this.enableLogin();
            } else {
              this.disabledTime = (disabledTime.diff(currentTime) / 1000).toFixed(0); // update the UI
            }
          }, 1000);
        }
      }
    },
    ...authStateMappers.mapActions(['doLoginAction', 'doEntraLoginAction']),

    showEntraError() {
      const error = getEntraLoginError();
      if (!error) {
        return;
      }
      if (error.status === 403) {
        this.$notify.error({
          title: this.$t('notification.login_error'),
          message: this.$t('notification.login_error_not_admin_text'),
          duration: 3000
        });
      } else {
        this.$notify.error({
          title: this.$t('notification.login_error'),
          message: this.$t('login.entra_login_error'),
          duration: 3000
        });
      }
    },

    submitUsername() {
      if (this.loginDisabled || this.isLoading) {
        return;
      }
      if (!this.login.username) {
        return;
      }
      this.isLoading = true;
      this.login.username = this.login.username.replace('/', '\\');

      authService.identify(this.login.username)
        .then((data) => {
          this.isLoading = false;
          const config = data.entraAuthenticationConfiguration;
          if (data.authenticationMethod === 'ENTRA') {
            this.entraConfig = config;
            this.step = 'entra';
          } else {
            this.step = 'password';
            this.$nextTick(() => {
              const el = document.getElementById('password');
              if (el) el.focus();
            });
          }
        })
        .catch(() => {
          this.isLoading = false;
          this.$notify.error({
            title: this.$t('notification.login_error'),
            message: this.$t('login.identify_error'),
            duration: 3000
          });
        });
    },

    startMicrosoftLogin() {
      if (this.isLoading || !this.entraConfig) {
        return;
      }
      this.isLoading = true;

      loginWithMicrosoftRedirect(this.entraConfig)
        .catch((error) => {
          this.isLoading = false;
          console.error('ENTRA REDIRECT START ERROR', error);
          this.$notify.error({
            title: this.$t('notification.login_error'),
            message: this.$t('login.entra_login_error'),
            duration: 3000
          });
        });
    },

    resetToUsername() {
      this.step = 'username';
      this.login.password = '';
      this.entraConfig = null;
    },

    dologin() {
      if (this.login.password.length <= 3) {
        return;
      }
      this.isLoading = true;
      this.doLoginAction(this.login)
        .then(() => {
          this.isLoading = false;
          removeLoginFailData(); // The login was successful -> remove the local storage data
          this.$router.push({name: 'current'});
        })
        .catch((error) => {
          this.isLoading = false;
          if (error && error.status && error.status === 403) {
            this.$notify.error({
              title: this.$t('notification.login_error'),
              message: this.$t('notification.login_error_not_admin_text'),
              duration: 3000
            });
            this.login.password = '';
          } else {
            this.$notify.error({
              title: this.$t('notification.login_error'),
              message: this.$t('notification.login_error_text'),
              duration: 3000
            });
            this.login.password = '';

                            let loginFailData = getLoginFailData();

                            if (!loginFailData) { // first failed attempt. Just save it
                                loginFailData = {
                                    count: 1,
                                    disabledTime: null,
                                    disabledCount: 1
                                };
                                setLoginFailData(loginFailData);
                            } else {
                                loginFailData.count += 1; // increase the failed attempts count
                                if (loginFailData.count >= 3 * loginFailData.disabledCount) { // check if the user tried it multiple times (3, 6, 9, 12 ... times)
                                    let currentTime = moment();
                                    let disabledMinutes = loginFailData.disabledCount * 2;
                                    loginFailData.disabledTime = currentTime.add(disabledMinutes, 'minutes').toISOString(); // calculate the time the login is disabled
                                    loginFailData.disabledCount += 1;
                                    setLoginFailData(loginFailData);
                                    this.disableLogin();
                                    this.checkDisabledLogin(); // start the timer to update the UI
                                } else {
                                    setLoginFailData(loginFailData); // just save the changes to localStorage
                                }
                            }
                        }
                    })
            }
        }
    }
</script>

<style lang="scss" scoped>
  .login-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-evenly;
    flex-direction: column;
    align-items: center;

    .logo {
      max-width: 20vw;
    }

    .error {
      color: red;
    }

    .login-input-wrap {
      min-width: 30vw;

      .input-form {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        padding: 1em 0 0 0;
        position: relative;

        .el-form-item {
          width: inherit;
        }

        .login-input {
          padding: 10px 5px 5px 5px;
        }

        .login-button {
          margin-top: 2em;
          align-self: center;
        }
      }
    }
  }

  .input-wrap {
    width: 100%;
    display: flex;
  }

  .back-button {
    margin-top: 0.5em;
    align-self: center;
  }

  .identified-user {
    align-self: stretch;
    margin: 10px 5px 0 5px;
    padding: 8px 12px;
    background-color: rgba(0, 0, 0, 0.05);
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    font-weight: 600;
    color: #606266;
  }
</style>
